import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export type PaymentRedirectOutcome = "success" | "failed" | "cancelled";

/**
 * Surfaces the outcome of a bKash checkout after the gateway redirects back.
 *
 * The backend already appends `?payment=success|failed|cancelled` to the return
 * URL, but nothing was reading it — users came back from bKash to a silent page
 * with no confirmation that their money went through.
 *
 * Shows exactly one toast per redirect, then strips the param so a refresh or
 * a back-navigation does not replay the message.
 *
 * @param onSuccess run after a confirmed payment — use it to refetch entitlements.
 */
export function usePaymentRedirectResult(onSuccess?: () => void) {
  const [searchParams, setSearchParams] = useSearchParams();
  const payment = searchParams.get("payment") as PaymentRedirectOutcome | null;
  // bKash's own statusMessage for a failed payment. Their API review requires
  // this to be shown to the customer rather than a generic error.
  const reason = searchParams.get("reason");

  // Guards against double-firing under React 18 StrictMode double-effects.
  const handledRef = useRef<string | null>(null);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!payment) return;
    if (handledRef.current === payment) return;
    handledRef.current = payment;

    if (payment === "success") {
      toast.success("Payment successful! Your access has been unlocked.");
      onSuccessRef.current?.();
    } else if (payment === "cancelled") {
      toast.info(
        reason || "Payment cancelled. You have not been charged.",
      );
    } else if (payment === "failed") {
      // Prefer bKash's statusMessage when present — it explains the actual
      // reason (insufficient balance, wrong PIN, limit exceeded, ...).
      toast.error(reason ? `Payment failed: ${reason}` : "Payment failed.", {
        description:
          "If your account was debited it will be reversed automatically. Contact support if it is not.",
        duration: 10000,
      });
    } else {
      return; // unknown value — leave the URL untouched
    }

    // Strip the params so the toast does not reappear on refresh.
    const next = new URLSearchParams(searchParams);
    next.delete("payment");
    next.delete("reason");
    setSearchParams(next, { replace: true });
  }, [payment, reason, searchParams, setSearchParams]);

  return payment;
}
